import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { webDtos } from '@vectronic/openApi-customer';
import { LoggedInUserService } from '@vectronic/shared/data-access/logged-in-user';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import {
  DialogService,
  SnackbarService,
  TableCollapseService,
  VectronicValidators,
} from '@vectronic/util';
import { ChipsCellComponent } from '../chips-cell/chips-cell.component';
import { ChipsCellItem, ChipsCellOption } from '../chips-cell/chips-cell.models';

@Component({
  selector: 'table-notification-chips-cell',
  template: `
    <table-chips-cell
      [items]="items()"
      [options]="options()"
      [showMore]="showMore()"
      [isCompactModeActivated]="isCompactModeActivated()"
      [canExpand]="canExpand()"
      [canEdit]="canEdit()"
      [overflowTooltip]="overflowTooltip()"
      [validators]="validators()"
      [collapsedMaxLength]="12"
      placeholder="Add notification"
      ariaLabel="Notifications"
      (add)="onAdd($event)"
      (selected)="onSelected($event)"
      (remove)="onRemove($event)"
      (toggleShowMore)="onToggleShowMore($event)"
      (chipClick)="onChipClick($event)"
      (keyUp)="onKeyUp($event)"
    ></table-chips-cell>
  `,
  imports: [ChipsCellComponent],
})
export class NotificationChipsCellComponent {
  private dialogService = inject(DialogService);
  private tableCollapseService = inject(TableCollapseService);
  snackbarService = inject(SnackbarService);
  loggedInUserService = inject(LoggedInUserService);

  device = input<webDtos.CollarExtended | webDtos.CollarAdmin | webDtos.TrapExtended>();
  rules = input<webDtos.NotificationRule[]>();
  addresses = input<webDtos.NotificationAddress[]>();
  readonly = input<boolean>(false);
  isCompactModeActivated = input<boolean>(false);

  addAddressAndRules = output<{
    address: webDtos.NotificationAddress;
    deviceIds: number[];
  }>();
  deleteRule = output<webDtos.NotificationRule>();
  addRule = output<webDtos.NotificationRule>();

  private selectionChanged = toSignal(this.tableCollapseService.onSelectionChanged$, {
    initialValue: undefined as number | string | undefined,
  });

  deviceId = computed(
    () =>
      (this.device() as webDtos.CollarExtended).id ||
      (this.device() as webDtos.CollarAdmin).idDevice,
  );

  rulesOfDevice = computed(() =>
    this.rules()?.filter(({ collarId }) => collarId === this.deviceId()),
  );

  isGuest = computed(() => this.device()['role'] === 'READER');
  canExpand = computed(() => !this.isGuest());
  canEdit = computed(() => !this.readonly());

  showMore = computed(() => {
    this.selectionChanged();
    return this.tableCollapseService.isNotificationSelectedCollarId(this.deviceId());
  });

  filteredAddresses = computed(() => {
    const addressIds = this.rulesOfDevice()?.map((rule) => rule.notificationAddressId) || [];
    return this.addresses()?.filter(({ addressId }) => !addressIds.includes(addressId)) || [];
  });

  items = computed<ChipsCellItem<webDtos.NotificationRule>[]>(() =>
    (this.rulesOfDevice() ?? []).map((rule) => ({
      id: rule.id,
      label: rule.notificationAddressValue,
      tooltip: rule.notificationAddressValue,
      data: rule,
    })),
  );

  options = computed<ChipsCellOption<webDtos.NotificationAddress>[]>(() =>
    (this.filteredAddresses() ?? []).map((address) => ({
      value: address.value,
      label: address.value,
      data: address,
    })),
  );

  overflowTooltip = computed(() =>
    (this.rulesOfDevice() ?? [])
      .map(({ notificationAddressValue }) => notificationAddressValue)
      .join(', '),
  );

  validators = computed<ValidatorFn[]>(() => [this.validator.bind(this)]);

  onAdd(value: string) {
    this.addMultiple(value);
  }

  onSelected(address: webDtos.NotificationAddress) {
    this.addRule.emit(this.createRule(address.addressId));
  }

  onRemove(rule: webDtos.NotificationRule) {
    this.dialogService.open(ConfirmationDialogComponent, {
      afterConfirm: () => this.deleteRule.emit(rule),
    });
  }

  onToggleShowMore(expand: boolean) {
    this.tableCollapseService.setNotificationSelectedCollarId(expand ? this.deviceId() : undefined);
  }

  onChipClick(rule: webDtos.NotificationRule) {
    this.copyToClipboard(rule.notificationAddressValue);
  }

  onKeyUp(value: string) {
    this.addMultiple(value);
  }

  private createRule(addressId: number): webDtos.NotificationRule {
    return {
      collarId: this.deviceId(),
      notificationAddressId: addressId,
      activity: true,
      gpsPosition: true,
      gsmQuality: true,
      mortality: true,
      mortalityImplant: true,
      proximity: true,
      separation: true,
      trapEvent: true,
      vaginalImplant: true,
      virtualFence: true,
      command: true,
    };
  }

  private addMultiple(enteredValue: string) {
    const delimiterRegex = /[,\s;]+/;
    const listOfNotifications = enteredValue?.split(delimiterRegex)?.filter(Boolean);
    listOfNotifications?.forEach((notification) => {
      const existingAddress = this.addresses()?.find(({ value }) => value === notification);
      if (existingAddress) {
        this.addRule.emit(this.createRule(existingAddress.addressId));
      } else {
        this.addAddressAndRules.emit({
          address: {
            type: VectronicValidators.getAddressType(notification),
            value: notification,
          },
          deviceIds: [this.deviceId()],
        });
      }
    });
  }

  private copyToClipboard(str: string) {
    if (this.loggedInUserService.isAdmin()) {
      navigator.clipboard.writeText(str).then(
        () => this.snackbarService.success('copied to clipboard'),
        () => this.snackbarService.error('unable to copy to clipboard'),
      );
    }
  }

  private validator(control: AbstractControl): ValidationErrors | null {
    if (typeof control.value !== 'string') return { invalidPhone: true };
    const type = VectronicValidators.getAddressType(control.value);
    if (type === 'EMAIL') {
      return VectronicValidators.isValidEmail(control.value) ? null : { pattern: true };
    } else {
      return VectronicValidators.isValidPhone(control.value) ? null : { invalidPhone: true };
    }
  }
}
